import { ProjectStatus } from '@/modules/projects/types/project';

/** Overhead kind: charged to the client (income) or absorbed by the company (cost). */
export enum OverheadType {
  Income = 'INCOME',
  Cost = 'COST',
}

/**
 * Overhead lifecycle status.
 * Income overheads: Pending → Invoiced (backend-driven via invoice generation).
 * Cost overheads: Planned ↔ Paid (manual).
 */
export enum OverheadStatus {
  Pending = 'PENDING',
  Invoiced = 'INVOICED',
  Planned = 'PLANNED',
  Paid = 'PAID',
}

/**
 * How an overhead repeats. One-time is the default; the other frequencies
 * materialize one occurrence per interval from the start date to the end date.
 */
export enum OverheadRecurrenceFrequency {
  OneTime = 'ONE_TIME',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
  Yearly = 'YEARLY',
}

/** Short project info attached to an overhead entry. */
export interface OverheadProject {
  id: number;
  name: string;
  colorHex?: string;
  logoUrl?: string | null;
  status: ProjectStatus | null;
}

/** Reference to the invoice an income overhead was included in. */
export interface OverheadInvoiceRef {
  id: number;
  number: string | null;
}

export interface OverheadListItem {
  id: number;
  type: OverheadType;
  name: string;
  notes: string | null;
  /** Calendar date of the overhead (ISO string, start-of-day UTC). */
  date: string;
  /** Flat amount in the project's currency on the overhead date. Negative allowed for income overheads (discounts). */
  amount: number;
  currencyCode: string;
  project: OverheadProject;
  clientName: string | null;
  status: OverheadStatus;
  /** Set only when status is Invoiced. */
  invoice: OverheadInvoiceRef | null;
  /** Set only for cost overheads with status Paid (ISO date). */
  paidAt: string | null;
  /**
   * Non-null when this entry is one occurrence of a recurring series.
   * The backend materializes one overhead per interval on creation; edits and
   * deletes affect single occurrences only (v1).
   */
  seriesId: string | null;
}

/** Aggregated amount + entry count for one summary bucket. */
export interface OverheadSummaryBucket {
  amount: number;
  count: number;
}

/**
 * Summary totals for the current filter selection, converted by the backend
 * to the organization's default currency.
 */
export interface OverheadsSummary {
  /** Organization default currency the totals are converted to. */
  currencyCode: string;
  incomePending: OverheadSummaryBucket;
  incomeInvoiced: OverheadSummaryBucket;
  costPlanned: OverheadSummaryBucket;
  costPaid: OverheadSummaryBucket;
}

/** Domain-shaped filters for the overheads search endpoint. */
export interface GetOverheadsFilter {
  searchString?: string;
  dateFrom?: string;
  dateTo?: string;
  projectIds?: number[];
  clientIds?: number[];
  types?: OverheadType[];
  statuses?: OverheadStatus[];
}
